import { Navigate } from "react-router-dom";
import Footer from "./components/Footer";
import { SkipLink } from "./components/primitives";
import Header from "./components/header";
import Hero from "./components/hero";
import TrustedBy from "./components/trusted-by";
// Só as perguntas que a home realmente mostra: dado estruturado de FAQ precisa
// bater com o conteúdo visível da página. As dezesseis seguem no schema de
// /faq, que é onde todas aparecem.
import { homeItems as faqItems } from "./components/faq";
import WhatsAppButton from "./components/WhatsAppButton";
import { SEO } from "@/components/seo";
import { useLandingReviews } from "./components/reviews/use-reviews";
import Faq from "./components/faq";
import HowItWorks from "./components/how-it-works";
import DataTrust from "./components/data-trust";
import Features from "./components/features";
import Pricing from "./components/pricing";
import CaseIeq from "./components/case-ieq";
import Testimonials from "./components/testimonials";
import FinalCta from "./components/final-cta";
import StickyCta from "./components/sticky-cta";
import Comparison from "./components/comparison";

export default function Landing() {
  // O mesmo resumo que alimenta o selo do Hero e o bloco de Depoimentos — o
  // request é compartilhado (cache de promise em lib/reviews.ts), então isso
  // não custa uma leitura extra de Firestore.
  const { data: reviews, suficiente: temReviews } = useLandingReviews();

  // Redireciona para /login se o app for aberto a partir da tela inicial (PWA / Standalone mode no iOS/Android)
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone ===
        true);

  if (isStandalone) {
    return <Navigate to="/dashboard" replace />;
  }

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Ecclesiato",
      url: "https://ecclesiato.com.br",
      description: "Sistema de gestão para igrejas",
      inLanguage: "pt-BR",
      publisher: {
        "@type": "Organization",
        name: "Ecclesiato",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Ecclesiato",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      browserRequirements: "Requires JavaScript. Requires HTML5.",
      url: "https://ecclesiato.com.br",
      screenshot: "https://ecclesiato.com.br/src/assets/DashboardDark.webp",
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "BRL",
        lowPrice: "97.00",
        highPrice: "1197.00",
        offerCount: "6",
        offers: [],
      },
      // Mesma frase de DESCRICAO_PRODUTO em scripts/seo-static.mjs.
      description:
        "Sistema de gestão para igrejas em português: membros, visitantes, células, ministério infantil com check-in por QR Code, escala de voluntários, eventos e controle financeiro de dízimos e ofertas. De R$ 97 a R$ 1.197 por mês até 3.000 pessoas ativas e plano sob medida acima disso, com os mais de 20 módulos em todos os planos.",
      featureList: [
        "Gestão completa de membros",
        "Relatórios e indicadores automáticos",
        "Controle de visitantes e apelos",
        "Eventos e ministérios integrados",
        "Escalas automáticas com regras de indisponibilidade e preferência",
        "Ministério infantil com check-in e retirada por QR Code",
        "Módulo de Finanças com conciliação OFX e exportação contábil",
        "Importação de planilhas com mapeamento de colunas",
        "Log de auditoria e permissões por papel",
        "Atualizações sob demanda",
      ],
      softwareVersion: "1.0",
      author: {
        "@type": "Organization",
        name: "Ecclesiato",
      },
      // Estrelas no resultado de busca.
      //
      // A proibição do Google a "self-serving reviews" (avaliação sobre você
      // no seu próprio site) vale só para os tipos `LocalBusiness` e
      // `Organization` — os demais tipos suportados, `SoftwareApplication`
      // incluído, seguem elegíveis ao rich snippet. É por isso que o
      // aggregateRating entra AQUI e não no bloco Organization mais abaixo:
      // lá ele seria ignorado e ainda por cima soaria como tentativa.
      //
      // Só declara quando existe nota real. `aggregateRating` com
      // reviewCount 0 é motivo de penalidade manual de dado estruturado.
      ...(temReviews && reviews
        ? {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: reviews.average,
              reviewCount: reviews.total,
              bestRating: 5,
              worstRating: 1,
            },
            review: reviews.items.slice(0, 5).map((r) => ({
              "@type": "Review",
              reviewRating: {
                "@type": "Rating",
                ratingValue: r.rating,
                bestRating: 5,
                worstRating: 1,
              },
              author: { "@type": "Person", name: r.authorName },
              reviewBody: r.comment,
              ...(r.createdAt
                ? { datePublished: r.createdAt.slice(0, 10) }
                : {}),
            })),
          }
        : {}),
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Ecclesiato",
      url: "https://ecclesiato.com.br",
      logo: "https://ecclesiato.com.br/logo.jpg",
      description:
        "Sistema completo para gestão de membros, visitantes e relatórios da sua igreja",
      foundingDate: "2024",
      // Espelha o ORGANIZACAO de scripts/seo-static.mjs.
      founder: {
        "@type": "Person",
        name: "Vinicius Graciano",
        url: "https://ecclesiato.com.br/about",
        memberOf: {
          "@type": "Church",
          name: "IEQ Central de Piracicaba",
        },
      },
      sameAs: ["https://instagram.com/ecclesiato"],
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+55-19-99337-6029",
        contactType: "customer service",
        email: "suporte@ecclesiato.com.br",
        areaServed: "BR",
        availableLanguage: ["Portuguese"],
        contactOption: "TollFree",
      },
      address: {
        "@type": "PostalAddress",
        addressCountry: "BR",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.title,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.content,
        },
      })),
    },
  ];

  return (
    <div className="landing-root flex min-h-screen flex-col bg-background">
      <SEO
        title="Ecclesiato | Sistema de Gestão para Igrejas"
        description="Sistema de gestão para igrejas: membros, visitantes, células, kids com check-in, escala de voluntários e dízimos num só lugar. Todos os módulos em qualquer plano, migração inclusa. Teste grátis."
        structuredData={structuredData}
        canonicalUrl="https://ecclesiato.com.br"
      />
      <SkipLink />
      <Header />
      {/* A ordem abaixo é deliberada: desejo antes de redução de risco.

          A home atende DOIS estágios de consciência (Schwartz), não os cinco:
          quem tem o problema e quem está avaliando o produto. Os outros já têm
          casa própria e é para lá que a home aponta, em vez de empilhar:

            - consciente da solução, comparando produto → /comparar (é lá que
              mora o quiz de recomendação: como o resultado é sempre
              Ecclesiato, aqui ele não filtrava ninguém);
            - quem quer profundidade de módulo e o detalhe da importação
              (mapeamento de colunas, lote piloto) → /features.

          Narrativa em nove blocos (eram treze): o que é → quem usa →
          contraste com o jeito de hoje → o produto por dentro (com o
          mecanismo das escalas dentro do bloco de escalas) → prova → como
          começa e se a equipe vai usar → segurança → oferta → dúvidas →
          fechamento. Saíram como seção própria, por repetirem a vizinha:
          o "antes e depois" (pain-solution.tsx, virou linhas do comparativo e
          segue na /about), o mecanismo (mechanism.tsx, foi para dentro de
          Recursos) e a adoção (adoption.tsx, foi para Como funciona).

          HowItWorks vem depois da prova, não logo após a dor: antes ele
          quebrava uma objeção ("dá trabalho trocar?") que o visitante ainda
          não tinha, porque ainda não queria o produto. Ao inserir seção nova,
          respeite esse fio E o estágio: seção que só serve quem já decidiu
          pertence a outra página. */}
      <main id="conteudo" className="flex-1">
        <Hero />
        <TrustedBy />
        <Comparison />
        <Features />
        <CaseIeq />
        <Testimonials />
        <HowItWorks />
        <DataTrust />
        <Pricing />
        <Faq />
        <FinalCta />
      </main>
      <Footer />
      <StickyCta />
      <WhatsAppButton />
    </div>
  );
}
